import React, { useState } from 'react';
import { useCart } from '../hooks/useCart';
import { Grid, List } from 'lucide-react';

// Components
import Header from '../components/common/Header';
import FeaturedProducts from '../components/product/FeaturedProducts';
import ProductFilters from '../components/product/ProductFilters';
import ProductCard from '../components/product/ProductCard';
import Pagination from '../components/product/Pagination';
import Toast from '../components/common/Toast';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Custom Hooks
import { useProducts, useCategories, useFeaturedProducts } from '../hooks/useProducts';

const HomePage = () => {
  const { addToCart } = useCart();
  
  // State management
  const [filters, setFilters] = useState({
    page: 1,
    limit: 12,
    category: '',
    minPrice: '',
    maxPrice: '',
    search: '',
    sort: 'createdAt',
    order: 'desc',
    featured: false
  });

  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  // Data fetching
  const { products, loading, error, pagination } = useProducts(filters);
  const { categories } = useCategories();
  const { featuredProducts } = useFeaturedProducts(6);

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1 // Reset to first page on filter change
    }));
  };

  const handlePageChange = (newPage) => {
    setFilters(prev => ({ ...prev, page: newPage }));
  };

  const clearFilters = () => {
    setFilters({
      page: 1,
      limit: 12,
      category: '',
      minPrice: '',
      maxPrice: '',
      search: '',
      sort: 'createdAt',
      order: 'desc',
      featured: false
    });
  };

  const toggleFilters = () => setShowFilters(!showFilters);

  const handleAddToCart = (product) => {
    addToCart({
      id: product._id,
      name: product.name,
      price: product.price,
      image: product.images?.[0] || '/placeholder-product.jpg',
      category: product.category,
      quantity: 1
    });
    setToastMessage(`${product.name} added to cart`);
    setShowToast(true);
  };

  // Loading skeleton
  const LoadingSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="bg-pureWhite rounded-lg p-4 shadow-sm animate-pulse">
          <div className="bg-lightGray h-48 rounded mb-4"></div>
          <div className="bg-lightGray h-4 rounded mb-2"></div>
          <div className="bg-lightGray h-4 rounded w-3/4"></div>
        </div>
      ))}
    </div>
  );

  // Error handling
  if (error) {
    return (
      <div className="min-h-screen bg-softRose flex items-center justify-center p-4">
        <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
          <h2 className="text-2xl font-bold text-slateText mb-4">Oops! Something went wrong</h2>
          <p className="text-slateText mb-6">{error.message || 'Failed to load products'}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-pinkAccent text-white px-6 py-2 rounded-lg hover:bg-pink-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-softRose">
      {/* Header */}
      <Header
        filters={filters}
        onFilterChange={handleFilterChange}
        showFilters={showFilters}
        onToggleFilters={toggleFilters}
      />

      {/* Featured Products */}
      <FeaturedProducts 
        products={featuredProducts} 
        onAddToCart={handleAddToCart} 
      />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Filters Sidebar */}
          <ProductFilters
            filters={filters}
            categories={categories}
            onFilterChange={handleFilterChange}
            onClearFilters={clearFilters}
            showFilters={showFilters}
          />

          {/* Products Section */}
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
              <div>
                <h1 className="text-2xl font-bold text-slateText">All Products</h1>
                <p className="text-slateText">
                  {loading ? 'Loading...' : `Showing ${products.length} of ${pagination.total} products`}
                </p>
              </div>

              <div className="flex items-center gap-2 bg-white p-1 rounded-lg shadow-sm">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-pinkAccent text-white' : 'text-slateText hover:bg-gray-50'}`}
                  aria-label="Grid view"
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-pinkAccent text-white' : 'text-slateText hover:bg-gray-50'}`}
                  aria-label="List view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Products Display */}
            {loading ? (
              <LoadingSkeleton />
            ) : products.length === 0 ? (
              <div className="bg-white rounded-lg p-8 text-center shadow-sm">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 mb-4">
                  Try adjusting your search or filter criteria
                </p>
                <button
                  onClick={clearFilters}
                  className="bg-pinkAccent text-white px-6 py-2 rounded-lg hover:bg-pink-700 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className={`grid gap-6 ${
                viewMode === 'grid' 
                  ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3' 
                  : 'grid-cols-1'
              }`}>
{products.map(product => (
  <ProductCard 
    key={product._id}
    product={product}
    viewMode={viewMode}
    onAddToCart={() => handleAddToCart(product)}
  />
))}
              </div>
            )}

            {/* Pagination */}
            {!loading && products.length > 0 && (
              <div className="mt-8">
                <Pagination
                  currentPage={filters.page}
                  totalPages={pagination.pages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <Toast 
          message={toastMessage}
          onClose={() => setShowToast(false)}
          duration={3000}
        />
      )}
    </div>
  );
};

export default HomePage;